import { type DateInput, dateToTimestamp } from "@/utils/transform/date";

/**
 * Shuffles the array and optionally slices the first `n` items from it
 */
export function shuffle<T>(array: T[] | Readonly<T[]>, n?: number) {
  // Recreate array to be mutated while shuffling
  const result = [...array];
  const length = array.length;
  // Default: use the entire length of the array for shuffling limit
  n = n ?? length;

  // Fisher Yates Shuffle
  // Performance: Only shuffle the first n items in the array
  for (let index = 0; index < n; index++) {
    const rand = index + Math.floor(Math.random() * (length - 1 - index + 1));
    [result[index], result[rand]] = [result[rand], result[index]];
  }

  return result.slice(0, n);
}

/**
 * .filter() Functions
 */

// Type-Safe Array function. Can be used in .filter() to remove nulls from an array
export const filterRemoveNull = <T>(item: T | null): item is T => item !== null;

// Type-Safe Array function. Can be used in .filter() to remove undefineds from an array
export const filterRemoveUndefined = <T>(item: T | undefined): item is T => item !== undefined;

// Type-Safe Array function. Can be used in .filter() to remove nulls and undefineds from an array
export const filterRemoveNullish = <T>(item: T | null | undefined): item is T =>
  filterRemoveUndefined(item) && filterRemoveNull(item);

/**
 * .sort() Functions
 */

export enum SortDirection {
  Ascending = 1,
  Descending = -1,
}
/**
 * Can be used in .sort() (either wrapped, of with .bind) to order numbers in either ascending or descending order.
 * This is an advanced usage, and recommended to use either the convenience directional methods below
 *
 * @example myArray.sort((a, b) => sortByNumber(true, a, b))
 * @example myArray.sort(sortByNumber.bind(null, true))
 */
export const sortByNumber = (sortDirection: SortDirection, a: number, b: number) => {
  // Push NaN to the end, regardless of sort direction
  if (Number.isNaN(a)) {
    return Infinity;
  }

  // Push numbers compared with NaN away from NaN
  if (Number.isNaN(b)) {
    return a * -sortDirection;
  }

  return (a - b) * sortDirection;
};

// Can be used in .sort() to order numbers in ascending order (smallest first)
export const sortByNumberAscending = (a: number, b: number) => sortByNumber(SortDirection.Ascending, a, b);
// Can be used in .sort() to order numbers in descending order (largest first)
export const sortByNumberDescending = (a: number, b: number) => sortByNumber(SortDirection.Descending, a, b);

// Can be used in .sort() to order date representations in ascending order (oldest first)
// Invalid Date inputs will be ranked last
export const sortByDateAscending = (a: DateInput, b: DateInput) =>
  sortByNumber(SortDirection.Ascending, dateToTimestamp(a), dateToTimestamp(b));

// Can be used in .sort() to order date representations in decending order (newest first).
// Invalid Date inputs will be ranked last
export const sortByDateDescending = (a: DateInput, b: DateInput) =>
  sortByNumber(SortDirection.Descending, dateToTimestamp(a), dateToTimestamp(b));
